/**
 * The record lane's one label → value row (SA-466).
 *
 * Lifted out of `StepDrawer`'s private `Kv`, which had the columns the right
 * way round and was already duplicated inside that one file. The Intake
 * panel and `TaskForm`'s Needs list had them INVERTED (`grid-cols-[1fr_auto]`):
 * an `auto` value track sizes to max-content, so one unbreakable value —
 * `desk@aidlogisticsconsortium.example` — made the grid wider than the
 * enquiry column's 330px content box and painted past the card's edge, while
 * the same pressure squeezed the label column to min-content and wrapped
 * "Cargo description" onto two lines.
 *
 * `minmax(6.5rem, max-content)` keeps a label on one line and never lets its
 * column collapse; the value track is the flexible one and wraps inside the
 * card. `dt` and `dd` are DIRECT children of the `dl` — every caller's tests
 * read a fact as `dt.nextElementSibling`, so no `contents` wrapper.
 */
import { Fragment, type ReactNode } from 'react'
import { cn } from '@/utils'

export interface Fact {
  /** React key and the row's identity. */
  key: string
  label: string
  value: ReactNode
  /** Rendered after the value INSIDE the wrapping `dd` — a chip that must
   * wrap with the value rather than ride off the card's edge. */
  after?: ReactNode
  /** `title` on the `dd`. Pass it only where the value is plain text. */
  title?: string
  /** A table or other wide value: the `dt` and the `dd` each span both columns, so the value
   * draws full width under its label (SA-1334, R7). Still direct children of the `dl`. */
  wide?: boolean
}

export function FactList({ facts, className }: { facts: Fact[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-[minmax(6.5rem,max-content)_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 text-[13px]', className)}>
      {facts.map((f) => (
        <Fragment key={f.key}>
          <dt className={cn('pt-px text-[12.5px] text-muted-foreground', f.wide && 'col-span-2')}>{f.label}</dt>
          <dd className={cn('min-w-0 text-foreground', f.wide ? 'col-span-2' : '[overflow-wrap:anywhere]')} title={f.title}>
            {f.value}
            {f.after}
          </dd>
        </Fragment>
      ))}
    </dl>
  )
}
